import { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { extractTextFromFile } from "../course/fileTextExtractor";
import { useTexts } from "../hooks/useTexts";

const lightTheme = {
    containerBg: "transparent",
    chatBg: "#ffffff",
    userMsg: "#DCF8C6",
    botMsg: "#f4f4f4",
    text: "#111111",
    inputBg: "#ffffff",
    border: "#e0e0e0",
};

const theme = lightTheme;

function parseMarkdown(text: string) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
            <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
            <span key={i}>{part}</span>
        )
    );
}

type FileItem = { name: string; path: string };
type TopicProps = { id: string; title: string; files?: FileItem[] };

type ChatbotProps = {
    topic?: TopicProps;
    showHeader?: boolean;
};

export default function Chatbot({ topic, showHeader = true }: ChatbotProps) {
    const { getText } = useTexts();
    const [messages, setMessages] = useState<{ role: string; text: string }[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (messages.length === 0) {
            if (topic) {
                setMessages([
                    {
                        role: "assistant",
                        text: getText("chatbot_welcome_topic", `Hallo! Ich bin dein KI-Assistent für das Thema **${topic.title}**. Stelle mir Fragen zu diesem Thema und ich werde sie basierend auf den hochgeladenen Materialien beantworten.`),
                    },
                ]);
            } else {
                setMessages([
                    {
                        role: "assistant",
                        text: getText("chatbot_welcome_general", "Hallo! Ich bin dein KI-Assistent für islamisches Wissen. Stelle mir Fragen zu islamischen Themen und ich werde sie basierend auf authentischen Quellen beantworten. Was möchtest du wissen?"),
                    },
                ]);
            }
        }
    }, [messages.length, topic, getText]);

    const getAllTopicText = async () => {
        if (!topic?.files || topic.files.length === 0) return "";
        let combinedText = "";
        for (const file of topic.files) {
            const storageRef = ref(storage, file.path);
            const url = await getDownloadURL(storageRef);
            try {
                const text = await extractTextFromFile(url, file.name);
                combinedText += text + "\n\n";
            } catch (err) {
                console.warn(`Text konnte nicht aus ${file.name} extrahiert werden`, err);
            }
        }
        return combinedText;
    };

    const sendMessage = async () => {
        if (!input.trim()) return;

        setMessages((prev) => [...prev, { role: "user", text: input }]);
        setInput("");
        setLoading(true);

        try {
            const materials = topic ? await getAllTopicText() : "";
            const result: any = await httpsCallable(getFunctions(app), "generateAIResponse")({
                question: input,
                topic: topic?.title,
                materials,
            });
            const text = result.data?.text ?? getText("chatbot_no_response", "Keine Antwort erhalten.");
            setMessages((prev) => [...prev, { role: "assistant", text }]);
        } catch (err: any) {
            setMessages((prev) => [...prev, { role: "assistant", text: getText("chatbot_error", "Fehler: ") + err?.message }]);
        }
        setLoading(false);
    };

    const styles = {
        container: {
            width: "100%",
            maxWidth: 900,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column" as const,
            gap: 20,
            background: theme.containerBg,
            color: theme.text,
            padding: 20,
            borderRadius: 16,
        },
        header: {
            textAlign: "center" as const,
            marginBottom: 20,
            paddingBottom: 20,
            borderBottom: `1px solid ${theme.border}`,
        },
        headerTitle: {
            fontSize: "2.2rem",
            fontWeight: 800,
            marginBottom: 12,
            background: "linear-gradient(135deg, #4CAF50, #2196F3)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap" as const,
        },
        topicBadge: {
            fontSize: "1rem",
            background: "rgba(76, 175, 80, 0.15)",
            color: "#4CAF50",
            padding: "6px 14px",
            borderRadius: 20,
            border: "1px solid rgba(76, 175, 80, 0.3)",
            fontWeight: 600,
        },
        headerSubtitle: {
            fontSize: "1.1rem",
            color: "#475569",
            lineHeight: 1.6,
            maxWidth: 700,
            margin: "0 auto",
        },
        chatWindow: {
            flex: 1,
            minHeight: 400,
            maxHeight: "60vh",
            overflowY: "auto" as const,
            padding: 20,
            background: theme.chatBg,
            borderRadius: 12,
            display: "flex",
            flexDirection: "column" as const,
            gap: 16,
            border: `1px solid ${theme.border}`,
        },
        message: {
            maxWidth: "85%",
            padding: "14px 18px",
            borderRadius: 16,
            fontSize: "1.05rem",
            lineHeight: 1.6,
            whiteSpace: "pre-wrap" as const,
            color: theme.text,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        },
        loading: {
            fontStyle: "italic",
            opacity: 0.7,
            textAlign: "center" as const,
            padding: 20,
        },
        inputRow: {
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
        },
        textarea: {
            flex: 1,
            padding: 14,
            borderRadius: 12,
            border: `2px solid ${theme.border}`,
            background: theme.inputBg,
            color: theme.text,
            fontSize: "1.05rem",
            resize: "none" as const,
            lineHeight: 1.5,
            minHeight: 60,
            fontFamily: "inherit",
        },
        button: {
            padding: "14px 28px",
            background: "linear-gradient(135deg, #4CAF50, #2E7D32)",
            color: "#fff",
            border: "none",
            borderRadius: 12,
            fontSize: "1.05rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
            minWidth: 100,
            height: "fit-content",
        },
        footer: {
            marginTop: 20,
            paddingTop: 20,
            borderTop: `1px solid ${theme.border}`,
        },
        tips: {
            fontSize: "0.95rem",
            color: "#475569",
        },
        tipsList: {
            marginTop: 8,
            paddingLeft: 20,
            lineHeight: 1.6,
        },
    };

    return (
        <div style={styles.container}>
            {showHeader && (
                <div style={styles.header}>
                    <h1 style={styles.headerTitle}>
                        {getText("chatbot_title", "🤖 AI Chatbot")}
                        {topic && <span style={styles.topicBadge}>{getText("chatbot_topic_prefix", "Thema:")} {topic.title}</span>}
                    </h1>
                    <p style={styles.headerSubtitle}>
                        {topic
                            ? getText("chatbot_subtitle_topic", `Stelle Fragen zum Thema "${topic.title}". Der Chatbot beantwortet sie ausschließlich auf Basis der hochgeladenen Materialien.`)
                            : getText("chatbot_subtitle_general", "Stelle Fragen zu islamischen Themen und erhalte präzise Antworten basierend auf authentischen Quellen. Der Chatbot ist 24/7 verfügbar.")}
                    </p>
                </div>
            )}

            <div style={styles.chatWindow}>
                {messages.map((m, i) => (
                    <div
                        key={i}
                        style={{
                            ...styles.message,
                            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                            background: m.role === "user" ? theme.userMsg : theme.botMsg,
                        }}
                    >
                        {m.text.split("\n").map((line, idx) => (
                            <div key={idx}>{parseMarkdown(line)}</div>
                        ))}
                    </div>
                ))}
                {loading && <div style={styles.loading}>{getText("chatbot_typing", "💬 KI schreibt…")}</div>}
            </div>

            <div style={styles.inputRow}>
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={topic ? getText("chatbot_placeholder_topic", `Frage zum Thema "${topic.title}" stellen…`) : getText("chatbot_placeholder_general", "Frage zu islamischen Themen stellen…")}
                    style={styles.textarea}
                    rows={2}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                />
                <button onClick={sendMessage} disabled={!input.trim() || loading} style={{ ...styles.button, opacity: input.trim() && !loading ? 1 : 0.6 }}>
                    {loading ? "..." : getText("chatbot_send", "Senden")}
                </button>
            </div>

            {showHeader && (
                <div style={styles.footer}>
                    <div style={styles.tips}>
                        <strong>{getText("chatbot_tips_title", "💡 Tipps:")}</strong>
                        <ul style={styles.tipsList}>
                            <li>{getText("chatbot_tip_1", "Stelle spezifische Fragen für genauere Antworten")}</li>
                            {topic && <li>{getText("chatbot_tip_2_topic", "Der Bot antwortet nur basierend auf den hochgeladenen Materialien")}</li>}
                            <li>{getText("chatbot_tip_3", "Du kannst nach Quellenangaben fragen")}</li>
                            <li>{getText("chatbot_tip_4", "Nutze Shift+Enter für Zeilenumbrüche")}</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}